import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Box } from "@material-ui/core"
import {
  borderRadiusBoxes,
  themePalette,
  webkitScrollbar,
  webkitscrollbarTrack,
} from "./styles"
import MeterCard, { CHILD_ROW_ESTIMATED_HEIGHT, PARENT_ROW_ESTIMATED_HEIGHT } from "./MeterCard"

const LIST_PADDING = 12
const CARD_GAP = 9
const OVERSCAN_PX = 420

// Aplana el arbol filtrado a tarjetas visuales.
const buildVisibleMeterCards = (filteredDevices, expandedMeters, isFilteringDevices) => {
  const cards = []

  for (const device of filteredDevices) {
    for (const group of device.groups) {
      for (const meter of group.meters) {
        const childrenForCurrentView = isFilteringDevices ? meter.filteredChildren || [] : meter.children || []
        const visibleChildren =
          (isFilteringDevices || expandedMeters.has(meter.id)) && childrenForCurrentView.length
            ? childrenForCurrentView
            : []

        cards.push({
          id: meter.id,
          meter,
          visibleChildren,
          isExpanded: isFilteringDevices || expandedMeters.has(meter.id),
        })
      }
    }
  }

  return cards
}

// Estima la altura reservada para cada tarjeta virtualizada.
const estimateCardHeight = (card) =>
  PARENT_ROW_ESTIMATED_HEIGHT +
  (card.visibleChildren.length ? card.visibleChildren.length * CHILD_ROW_ESTIMATED_HEIGHT + 1 : 0) +
  CARD_GAP

// Busca rapidamente desde que item conviene empezar a renderizar.
const findStartIndex = (offsets, heights, target) => {
  let low = 0
  let high = offsets.length - 1
  let result = offsets.length

  while (low <= high) {
    const middle = Math.floor((low + high) / 2)
    const itemEnd = offsets[middle] + heights[middle]

    if (itemEnd >= target) {
      result = middle
      high = middle - 1
    } else {
      low = middle + 1
    }
  }

  return result
}

// Lista virtualizada de medidores.
// Esta pieza concentra la performance del panel izquierdo y evita renderizar toda la lista a la vez.
function VirtualizedMetersList({
  isMobile,
  mobileView,
  leftPanelScrollRef,
  filteredDevices,
  selectedMeterId,
  setSelectedMeter,
  setMobileView,
  renderCloudAccessBadge,
  renderAssignmentNodeIcon,
  expandedMeters,
  setExpandedMeters,
  isFilteringDevices,
  getStatusIcon,
}) {
  const listScrollRef = useRef(null)
  const [scrollTop, setScrollTop] = useState(0)
  const [viewportHeight, setViewportHeight] = useState(0)

  const setListRef = useCallback((node) => {
    listScrollRef.current = node
    if (leftPanelScrollRef && typeof leftPanelScrollRef === "object") {
      leftPanelScrollRef.current = node
    }
    if (node) {
      setViewportHeight(node.clientHeight)
      setScrollTop(node.scrollTop)
    }
  }, [leftPanelScrollRef])

  const meterCards = useMemo(
    () => buildVisibleMeterCards(filteredDevices, expandedMeters, isFilteringDevices),
    [expandedMeters, filteredDevices, isFilteringDevices]
  )

  useEffect(() => {
    const node = listScrollRef.current
    if (!node) return undefined

    const updateViewportHeight = () => {
      setViewportHeight(node.clientHeight)
    }

    updateViewportHeight()

    if (typeof ResizeObserver === "function") {
      const observer = new ResizeObserver(updateViewportHeight)
      observer.observe(node)

      return () => observer.disconnect()
    }

    window.addEventListener("resize", updateViewportHeight)
    return () => window.removeEventListener("resize", updateViewportHeight)
  }, [])

  const virtualizedCards = useMemo(() => {
    const heights = []
    const offsets = []
    let accumulatedHeight = LIST_PADDING

    for (const card of meterCards) {
      offsets.push(accumulatedHeight)
      const cardHeight = estimateCardHeight(card)
      heights.push(cardHeight)
      accumulatedHeight += cardHeight
    }

    return {
      heights,
      offsets,
      totalHeight: accumulatedHeight + LIST_PADDING,
    }
  }, [meterCards])

  const { visibleCards, totalHeight } = useMemo(() => {
    if (meterCards.length === 0) {
      return { visibleCards: [], totalHeight: LIST_PADDING * 2 }
    }

    const { heights, offsets, totalHeight: nextTotalHeight } = virtualizedCards
    const viewportTop = Math.max(0, scrollTop - OVERSCAN_PX)
    const viewportBottom = scrollTop + Math.max(viewportHeight, 0) + OVERSCAN_PX
    const startIndex = Math.max(0, findStartIndex(offsets, heights, viewportTop) - 1)
    let endIndex = startIndex

    while (endIndex < meterCards.length && offsets[endIndex] < viewportBottom) {
      endIndex += 1
    }

    const nextVisibleCards = []

    for (let index = startIndex; index < Math.min(endIndex + 1, meterCards.length); index += 1) {
      nextVisibleCards.push({
        card: meterCards[index],
        top: offsets[index],
      })
    }

    return {
      visibleCards: nextVisibleCards,
      totalHeight: nextTotalHeight,
    }
  }, [meterCards, scrollTop, viewportHeight, virtualizedCards])

  const handleScroll = useCallback((event) => {
    setScrollTop(event.currentTarget.scrollTop)
  }, [])

  const handleSelectMeter = useCallback((meter) => {
    setSelectedMeter(meter)
    if (isMobile) setMobileView("detail")
  }, [isMobile, setMobileView, setSelectedMeter])

  const handleToggleExpanded = useCallback((meterId) => {
    setExpandedMeters((prev) => {
      const next = new Set(prev)
      next.has(meterId) ? next.delete(meterId) : next.add(meterId)
      return next
    })
  }, [setExpandedMeters])

  return (
    <Box
      ref={setListRef}
      onScroll={handleScroll}
      sx={{
        overflowY: "scroll",
        flex: 1,
        minHeight: 0,
        display: isMobile && mobileView !== "list" ? "none" : "block",
        "&::-webkit-scrollbar": { ...webkitScrollbar },
        "&::-webkit-scrollbar-track": { ...webkitscrollbarTrack },
        "&::-webkit-scrollbar-thumb": { background: themePalette.primary.main },
      }}
    >
      {meterCards.length === 0 ? (
        <Box sx={{ padding: `${LIST_PADDING}px` }}>
          <Box
            sx={{
              margin: "0.5rem",
              padding: "1rem",
              borderRadius: borderRadiusBoxes,
              border: `1px solid ${themePalette.border.soft}`,
              backgroundColor: themePalette.surface.soft,
              color: themePalette.text.disabled,
            }}
          >
            No hay nodos disponibles para mostrar.
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            position: "relative",
            minHeight: `${Math.max(totalHeight, viewportHeight || 0)}px`,
          }}
        >
          {visibleCards.map(({ card, top }) => (
            <Box
              key={card.id}
              sx={{
                position: "absolute",
                top: `${top}px`,
                left: `${LIST_PADDING}px`,
                right: `${LIST_PADDING}px`,
                paddingBottom: `${CARD_GAP}px`,
                boxSizing: "border-box",
              }}
            >
              <MeterCard
                meter={card.meter}
                visibleChildren={card.visibleChildren}
                isExpanded={card.isExpanded}
                isFilteringDevices={isFilteringDevices}
                isMobile={isMobile}
                selectedMeterId={selectedMeterId}
                onSelectMeter={handleSelectMeter}
                onToggleExpanded={handleToggleExpanded}
                renderCloudAccessBadge={renderCloudAccessBadge}
                renderAssignmentNodeIcon={renderAssignmentNodeIcon}
                getStatusIcon={getStatusIcon}
              />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

export default memo(VirtualizedMetersList)
